// FadeInSection — lightweight IntersectionObserver-based fade-in wrapper.
// No external library required. Respects `prefers-reduced-motion`.
import React, { useEffect, useRef, useState } from 'react';

/**
 * Wraps children and fades them in from ~24px below once they enter the viewport.
 * @param {number} delay - ms delay before animation starts
 * @param {string} className - extra classes to merge
 */
export const FadeInSection = ({ children, delay = 0, className = '', as: Tag = 'div', ...rest }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    // Respect user's motion preference
    const mq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    if (mq?.matches) { setReducedMotion(true); setVisible(true); return; }

    const el = ref.current;
    if (!el) return;

    // Fallback for very old browsers
    if (!('IntersectionObserver' in window)) { setVisible(true); return; }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -60px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const style = reducedMotion
    ? undefined
    : {
        opacity: visible ? 1 : 0,
        transform: visible ? 'translate3d(0,0,0)' : 'translate3d(0, 24px, 0)',
        transition: `opacity 700ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms, transform 700ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
        willChange: 'opacity, transform',
      };

  return (
    <Tag ref={ref} className={className} style={style} {...rest}>
      {children}
    </Tag>
  );
};

export default FadeInSection;
